import { useState, useEffect, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../../components/ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { format, parseISO, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { FileText, Send, Loader2, Calendar, User, MapPin, Monitor, Clock, Image as ImageIcon, Users, CheckCircle, XCircle, Mail, ChevronsUpDown, Check, Stethoscope, PartyPopper, Palmtree, ChevronDown, ChevronRight, Building2, Printer, Download, FileSpreadsheet, Eye, Table, Forward, MessageSquare, MessageSquarePlus, Trash2, Reply, Search, ClipboardList } from "lucide-react";
import { cn } from "../../lib/utils";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

// Day type configuration
const DAY_TYPE_CONFIG = {
  sick_day: { label: "SICK DAY", icon: Stethoscope, color: "text-red-500", bgColor: "bg-red-50", borderColor: "border-red-200" },
  holiday: { label: "HOLIDAY", icon: PartyPopper, color: "text-purple-500", bgColor: "bg-purple-50", borderColor: "border-purple-200" },
  vacation: { label: "VACATION", icon: Palmtree, color: "text-green-500", bgColor: "bg-green-50", borderColor: "border-green-200" },
};

// Forward Email Dialog Component
const ForwardEmailDialog = ({ log, open, onClose, teams, users }) => {
  const [email, setEmail] = useState("");
  const [sendToManager, setSendToManager] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email && !sendToManager) {
      toast.error("Please enter an email address or select 'CC Manager'");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/admin/forward-log`, {
        log_id: log.id,
        user_id: log.user_id,
        email: email || null,
        cc_manager: sendToManager
      });
      toast.success("Log forwarded successfully!");
      onClose();
      setEmail("");
      setSendToManager(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to forward log");
    } finally {
      setSending(false);
    }
  };

  // Get team names for the user
  const userTeamNames = (log.team_ids || [])
    .map(tid => teams.find(t => t.id === tid)?.name)
    .filter(Boolean)
    .join(", ") || "No Team";

  const rtIds = Array.isArray(log.reports_to) ? log.reports_to : (log.reports_to ? [log.reports_to] : []);
  const managerName = rtIds.length > 0 ? rtIds.map(id => users?.find(u => u.id === id)?.full_name).filter(Boolean).join(", ") : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Forward className="h-5 w-5" />
            Forward Log
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="font-medium text-slate-900">{log.user_name}</p>
            <p className="text-sm text-slate-500">{log.log_date} • {userTeamNames}</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="forward-email">Email Address</Label>
            <Input
              id="forward-email"
              type="email"
              placeholder="recipient@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forward-email-input"
            />
            <p className="text-xs text-slate-500">Leave blank if sending only to Manager</p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="send-to-manager"
              checked={sendToManager}
              onCheckedChange={setSendToManager}
              data-testid="send-to-manager-checkbox"
            />
            <Label htmlFor="send-to-manager" className="text-sm cursor-pointer">
              CC Manager{managerName ? ` (${managerName})` : ""}
            </Label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending} data-testid="send-forward-btn">
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Log Notes Dialog Component
const LogNotesDialog = ({ log, open, onClose, onNotesChange }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (open && log) {
      fetchNotes();
    }
  }, [open, log]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/logs/${log.id}/notes`);
      setNotes(res.data.notes || []);
    } catch (err) {
      toast.error("Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/logs/${log.id}/notes`, {
        log_id: log.id,
        note: newNote.trim()
      });
      setNotes([res.data.note, ...notes]);
      setNewNote("");
      toast.success("Note added successfully");
      if (onNotesChange) onNotesChange(log.id, notes.length + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    setDeleting(noteId);
    try {
      await axios.delete(`${API}/logs/notes/${noteId}`);
      const updatedNotes = notes.filter(n => n.id !== noteId);
      setNotes(updatedNotes);
      toast.success("Note deleted");
      if (onNotesChange) onNotesChange(log.id, updatedNotes.length);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete note");
    } finally {
      setDeleting(null);
    }
  };

  const formatNoteDate = (isoString) => {
    try {
      const date = parseISO(isoString);
      const estDate = toZonedTime(date, "America/New_York");
      return format(estDate, "MMM d, yyyy 'at' h:mm a");
    } catch {
      return "Unknown";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Notes for {log?.user_name}
          </DialogTitle>
          <p className="text-sm text-slate-500">{log?.log_date}</p>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Add new note */}
          <div className="space-y-2">
            <Label htmlFor="new-note">Add a Note</Label>
            <Textarea
              id="new-note"
              placeholder="Enter your note here..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={3}
              data-testid="new-note-input"
            />
            <Button 
              onClick={handleAddNote} 
              disabled={submitting || !newNote.trim()}
              size="sm"
              data-testid="add-note-btn"
            >
              {submitting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Adding...</>
              ) : (
                <><MessageSquarePlus className="mr-2 h-4 w-4" />Add Note</>
              )}
            </Button>
          </div>

          {/* Existing notes */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-slate-700 mb-3">
              {notes.length > 0 ? `Notes (${notes.length})` : "No notes yet"}
            </h4>
            
            {loading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : notes.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                No notes have been added for this log.
              </p>
            ) : (
              <div className="space-y-3">
                {notes.map((note) => (
                  <div key={note.id} className="bg-slate-50 rounded-lg p-3 relative group">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.note}</p>
                        <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                          <Badge variant="outline" className="text-xs">
                            {note.created_by_role === "ADMIN" ? "Admin" : "Team Leader"}
                          </Badge>
                          <span>{note.created_by_name}</span>
                          <span>•</span>
                          <span>{formatNoteDate(note.created_at)}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteNote(note.id)}
                        disabled={deleting === note.id}
                        data-testid={`delete-note-${note.id}`}
                      >
                        {deleting === note.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex justify-end pt-2 border-t">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Respond to User Dialog Component
const RespondDialog = ({ log, open, onClose, teams, users }) => {
  const [message, setMessage] = useState("");
  const [ccManager, setCcManager] = useState(false);
  const [sending, setSending] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes
      setMessage("");
      setCcManager(false);
      setSending(false);
    }
  }, [open]);

  // Get team names for the user
  const userTeamNames = (log?.team_ids || [])
    .map(tid => teams.find(t => t.id === tid)?.name)
    .filter(Boolean)
    .join(", ") || "No Team";

  const rtIds2 = Array.isArray(log?.reports_to) ? log.reports_to : (log?.reports_to ? [log.reports_to] : []);
  const managerName = rtIds2.length > 0 ? rtIds2.map(id => users?.find(u => u.id === id)?.full_name).filter(Boolean).join(", ") : null;

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    if (!log?.id || !log?.user_id) {
      toast.error("Invalid log data. Please close and try again.");
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/respond-to-user`, {
        log_id: log.id,
        user_id: log.user_id,
        message: message.trim(),
        cc_manager: ccManager
      }, { timeout: 30000 }); // 30 second timeout
      toast.success(response.data?.message || "Response sent successfully!");
      onClose();
    } catch (err) {
      console.error("Respond to user error:", err);
      if (err.code === 'ECONNABORTED') {
        toast.error("Request timed out. Please try again.");
      } else {
        toast.error(err.response?.data?.detail || "Failed to send response. Please try again.");
      }
      setSending(false);
    }
  };

  if (!log) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Reply className="h-5 w-5" />
            Respond to User
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="font-medium text-slate-900">{log.user_name}</p>
            <p className="text-sm text-slate-500">{log.user_email}</p>
            <p className="text-sm text-slate-500">{log.log_date} • {userTeamNames}</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="respond-message">Your Message</Label>
            <Textarea
              id="respond-message"
              placeholder="Enter your feedback or response..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              data-testid="respond-message-input"
            />
            <p className="text-xs text-slate-500">
              This message will be sent to {log.user_name} via email regarding their log for {log.log_date}.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="cc-manager"
              checked={ccManager}
              onCheckedChange={setCcManager}
              data-testid="cc-manager-checkbox"
            />
            <Label htmlFor="cc-manager" className="text-sm cursor-pointer">
              CC Manager{managerName ? ` (${managerName})` : ""}
            </Label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending || !message.trim()} data-testid="send-response-btn">
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send Response
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Date range presets
const getDateRangePresets = () => {
  const today = new Date();
  return [
    { 
      value: "today", 
      label: "Today",
      from: format(today, "yyyy-MM-dd"),
      to: format(today, "yyyy-MM-dd")
    },
    { 
      value: "yesterday", 
      label: "Yesterday",
      from: format(subDays(today, 1), "yyyy-MM-dd"),
      to: format(subDays(today, 1), "yyyy-MM-dd")
    },
    { 
      value: "this_week", 
      label: "This Week",
      from: format(startOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd"),
      to: format(endOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd")
    },
    { 
      value: "last_week", 
      label: "Last Week",
      from: format(startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), "yyyy-MM-dd"),
      to: format(endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), "yyyy-MM-dd")
    },
    {
      value: "mtd",
      label: "MTD (Month to Date)",
      from: format(startOfMonth(today), "yyyy-MM-dd"),
      to: format(today, "yyyy-MM-dd")
    },
    {
      value: "this_month",
      label: "This Month",
      from: format(startOfMonth(today), "yyyy-MM-dd"),
      to: format(endOfMonth(today), "yyyy-MM-dd")
    },
    { 
      value: "last_month", 
      label: "Last Month",
      from: format(startOfMonth(subMonths(today, 1)), "yyyy-MM-dd"),
      to: format(endOfMonth(subMonths(today, 1)), "yyyy-MM-dd")
    },
  ];
};

// Helper to format time in EST
const formatTimeEST = (isoString) => {
  const date = parseISO(isoString);
  const estDate = toZonedTime(date, "America/New_York");
  return format(estDate, "h:mm a") + " EST";
};

const formatDateEST = (isoString) => {
  const date = parseISO(isoString);
  const estDate = toZonedTime(date, "America/New_York");
  return format(estDate, "MMM d, yyyy h:mm a") + " EST";
};

// Component to display attachments
const AttachmentList = ({ attachments }) => {
  if (!attachments || attachments.length === 0) return null;
  
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((att) => (
        <a
          key={att.id}
          href={`${BASE_URL}${att.url}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded hover:bg-blue-100 transition-colors"
        >
          <ImageIcon className="h-3 w-3" />
          {att.original_name || "Image"}
        </a>
      ))}
    </div>
  );
};

// Collapsible Log Card
const LogCard = ({ log, teams = [], notesCount = 0, onNotesChange, forms = [], users = [], jdData = {}, isMultiDay = false, userLogTotals = {} }) => {
  const [expanded, setExpanded] = useState(false);
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [respondDialogOpen, setRespondDialogOpen] = useState(false);
  const [localNotesCount, setLocalNotesCount] = useState(notesCount);
  const dayTypeInfo = log.day_type ? DAY_TYPE_CONFIG[log.day_type] : null;
  const isNonWorkDay = !!dayTypeInfo;
  
  // Get user's form to show correct fields
  const userForm = forms.find(f => f.id === log.assigned_form_id);
  const formFields = userForm?.fields?.filter(f => f.field_type !== 'file') || [
    { id: 'tasks_today', label: 'Tasks Worked On Today' },
    { id: 'challenges', label: 'Challenges and Hurdles' },
    { id: 'plan_tomorrow', label: 'Plan for Tomorrow' },
    { id: 'ai_tools_used', label: 'AI Tools Discovery' }
  ];

  // Update local count when prop changes
  useEffect(() => {
    setLocalNotesCount(notesCount);
  }, [notesCount]);

  const handleNotesChange = (logId, newCount) => {
    setLocalNotesCount(newCount);
    if (onNotesChange) onNotesChange(logId, newCount);
  };

  return (
    <>
      <Card className={cn("transition-all", isNonWorkDay && dayTypeInfo.borderColor)}>
        <CardHeader 
          className="pb-2 cursor-pointer hover:bg-slate-50/50 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
              <div>
                <CardTitle className="text-base">{log.user_name}</CardTitle>
                <CardDescription className="text-xs">{log.user_email} • {format(parseISO(log.log_date), "MMM d, yyyy")}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Respond Button */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-slate-400 hover:text-green-600 hover:bg-green-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setRespondDialogOpen(true);
                }}
                title="Respond to user"
                data-testid={`respond-log-${log.id}`}
              >
                <Reply className="h-4 w-4" />
              </Button>
              {/* Notes Button */}
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "h-8 w-8 p-0 relative",
                  localNotesCount > 0 
                    ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50" 
                    : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setNotesDialogOpen(true);
                }}
                title={localNotesCount > 0 ? `${localNotesCount} note(s)` : "Add note"}
                data-testid={`notes-log-${log.id}`}
              >
                <MessageSquare className="h-4 w-4" />
                {localNotesCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    {localNotesCount > 9 ? "9+" : localNotesCount}
                  </span>
                )}
              </Button>
              {/* Forward Button */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setForwardDialogOpen(true);
                }}
                title="Forward this log"
                data-testid={`forward-log-${log.id}`}
              >
                <Forward className="h-4 w-4" />
              </Button>
              {isNonWorkDay ? (
                <Badge variant="outline" className={`${dayTypeInfo.bgColor} ${dayTypeInfo.color} ${dayTypeInfo.borderColor}`}>
                  <dayTypeInfo.icon className="h-3 w-3 mr-1" />
                  {dayTypeInfo.label}
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Submitted
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        
        {expanded && (
          <CardContent className="pt-2 space-y-4">
            {isNonWorkDay ? (
              <div className={`relative py-8 rounded-lg ${dayTypeInfo.bgColor} border-2 border-dashed ${dayTypeInfo.borderColor}`}>
                <div className="flex flex-col items-center justify-center">
                  <dayTypeInfo.icon className={`h-12 w-12 ${dayTypeInfo.color} mb-2`} />
                  <h2 className={`text-2xl font-bold ${dayTypeInfo.color} tracking-wider`}>
                    {dayTypeInfo.label}
                  </h2>
                </div>
              </div>
            ) : (
              <>
                {formFields.map((field) => {
                  const fieldValue = log[field.id];
                  const fieldAttachments = log[`${field.id}_attachments`];
                  
                  // Only show fields that have content
                  if (!fieldValue || (typeof fieldValue === 'string' && !fieldValue.trim())) return null;
                  
                  return (
                    <div key={field.id}>
                      <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                        {field.label}
                      </h4>
                      <div className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg prose prose-sm max-w-none" 
                           dangerouslySetInnerHTML={{ __html: fieldValue }} />
                      {fieldAttachments && <AttachmentList attachments={fieldAttachments} />}
                    </div>
                  );
                })}
              </>
            )}

            {/* JobDiva ATS vs Self-Reported — single day only (multi-day shown per user below logs) */}
            {!isMultiDay && jdData[log.user_id] && (() => {
              const jd = jdData[log.user_id];
              const selfSub = parseInt(log.num_submittals || 0);
              const selfInt = parseInt(log.num_interviews || 0);
              const hasMismatch = selfSub !== jd.submittals || selfInt !== jd.interviews;
              return (
                <div className={`rounded-lg p-3 border ${hasMismatch ? 'bg-amber-50 border-amber-300' : 'bg-indigo-50 border-indigo-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-indigo-700">
                      JobDiva ATS vs Self-Reported
                    </p>
                    {hasMismatch && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Mismatch</span>}
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className={`text-lg font-bold ${selfSub !== jd.submittals ? 'text-amber-700' : 'text-indigo-900'}`}>{jd.submittals}</p>
                      <p className="text-[10px] text-indigo-600 uppercase">JD Submittals</p>
                      {selfSub !== jd.submittals && <p className="text-[10px] text-slate-500">Self: {selfSub}</p>}
                    </div>
                    <div>
                      <p className={`text-lg font-bold ${selfInt !== jd.interviews ? 'text-amber-700' : 'text-indigo-900'}`}>{jd.interviews}</p>
                      <p className="text-[10px] text-indigo-600 uppercase">JD Interviews</p>
                      {selfInt !== jd.interviews && <p className="text-[10px] text-slate-500">Self: {selfInt}</p>}
                    </div>
                    <div>
                      <p className="text-lg font-bold text-indigo-900">{jd.hires}</p>
                      <p className="text-[10px] text-indigo-600 uppercase">JD Hires</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Metadata */}
            <div className="pt-3 border-t border-slate-100">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="flex items-center gap-2 text-slate-500">
                  <Clock className="h-3 w-3" />
                  <span>{formatTimeEST(log.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-500">
                  <Monitor className="h-3 w-3" />
                  <span>{log.browser_name} / {log.os_name}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-500">
                  <MapPin className="h-3 w-3" />
                  <span>{[log.geo_city, log.geo_region, log.geo_country].filter(Boolean).join(", ") || log.ip_address}</span>
                </div>
                <div className="text-slate-400">IP: {log.ip_address}</div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
      
      <ForwardEmailDialog
        log={log}
        open={forwardDialogOpen}
        onClose={() => setForwardDialogOpen(false)}
        teams={teams}
        users={users}
      />
      
      <LogNotesDialog
        log={log}
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        onNotesChange={handleNotesChange}
      />
      
      <RespondDialog
        log={log}
        open={respondDialogOpen}
        onClose={() => setRespondDialogOpen(false)}
        teams={teams}
        users={users}
      />
    </>
  );
};

export default function ReportsPage() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [managers, setManagers] = useState([]);
  const [jdData, setJdData] = useState({});
  const [loading, setLoading] = useState(true);
  const [sendingReport, setSendingReport] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const _rpf = (() => { try { return JSON.parse(sessionStorage.getItem('jtcrm_reports_filters') || '{}'); } catch { return {}; } })();
  const [selectedRange, setSelectedRange] = useState(_rpf.selectedRange || "today");
  const [selectedUser, setSelectedUser] = useState(_rpf.selectedUser || "all");
  const [selectedTeam, setSelectedTeam] = useState(_rpf.selectedTeam || "all");
  const [selectedManager, setSelectedManager] = useState(_rpf.selectedManager || "all");
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [managerSearchOpen, setManagerSearchOpen] = useState(false);
  const [expandedTeams, setExpandedTeams] = useState({});
  const [notesCounts, setNotesCounts] = useState({});
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [sendingIndividualReminder, setSendingIndividualReminder] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [productivityFilter, setProductivityFilter] = useState("");
  const [managerReportsDialogOpen, setManagerReportsDialogOpen] = useState(false);
  const [generatingManagerReports, setGeneratingManagerReports] = useState(false);
  const [managerReportsOption, setManagerReportsOption] = useState("download"); // download or email
  const [mtrDialogOpen, setMtrDialogOpen] = useState(false);
  const [generatingMtr, setGeneratingMtr] = useState(false);
  const [mtrOption, setMtrOption] = useState("download");
  // Reminder dialog state
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderUsers, setReminderUsers] = useState([]); // Can be single user or multiple users
  const [selectedReminderUsers, setSelectedReminderUsers] = useState([]); // Users selected via checkboxes
  const [reminderMessage, setReminderMessage] = useState("");
  const [sendingReminderDialog, setSendingReminderDialog] = useState(false);
  const [ccManager, setCcManager] = useState(false);
  // Reminder stats state
  const [reminderStats, setReminderStats] = useState({});
  
  // PDF Report dialog state
  const [pdfReportDialogOpen, setPdfReportDialogOpen] = useState(false);
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [selectedFormFields, setSelectedFormFields] = useState([]);
  const [ccEmails, setCcEmails] = useState("");
  const [sendingPdfReport, setSendingPdfReport] = useState(false);
  
  const reportRef = useRef(null);
  const searchRef = useRef(null);

  const DEFAULT_REMINDER_MESSAGE = "This is a friendly reminder to complete your daily work log.";

  const dateRangePresets = getDateRangePresets();
  const currentRange = dateRangePresets.find(r => r.value === selectedRange) || dateRangePresets[0];

  useEffect(() => {
    fetchData();
  }, [selectedRange, selectedUser, selectedTeam, selectedManager]);

  useEffect(() => {
    sessionStorage.setItem('jtcrm_reports_filters', JSON.stringify({ selectedRange, selectedUser, selectedTeam, selectedManager }));
  }, [selectedRange, selectedUser, selectedTeam, selectedManager]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("from_date", currentRange.from);
      params.append("to_date", currentRange.to);
      if (selectedUser !== "all") {
        params.append("user_id", selectedUser);
      }
      if (selectedTeam !== "all") {
        params.append("team_id", selectedTeam);
      }
      if (selectedManager !== "all") {
        params.append("manager_id", selectedManager);
      }

      const [logsRes, usersRes, teamsRes, formsRes] = await Promise.all([
        axios.get(`${API}/admin/logs?${params.toString()}`),
        axios.get(`${API}/users`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/forms`),
      ]);
      setLogs(logsRes.data);
      setTeams(teamsRes.data);
      setForms(formsRes.data || []);

      // Fetch JobDiva data
      try {
        const jdRes = await axios.get(`${API}/jobdiva/recruiter-activity?from_date=${currentRange.from}&to_date=${currentRange.to}`);
        setJdData(jdRes.data?.data || {});
      } catch { setJdData({}); }

      // Fetch notes counts for all logs
      if (logsRes.data.length > 0) {
        const logIds = logsRes.data.map(l => l.id).join(",");
        try {
          const countsRes = await axios.get(`${API}/logs/notes/count?log_ids=${logIds}`);
          setNotesCounts(countsRes.data.counts || {});
        } catch (e) {
          console.error("Failed to fetch notes counts", e);
        }
      } else {
        setNotesCounts({});
      }
      
      // Get all users (unfiltered) to extract unique managers
      const allUsers = usersRes.data;
      
      // Extract unique manager IDs from reports_to field (may be str or array)
      const managerIds = new Set();
      allUsers.forEach(user => {
        const rt = user.reports_to;
        const ids = Array.isArray(rt) ? rt : (rt ? [rt] : []);
        ids.forEach(id => { if (id) managerIds.add(id); });
      });
      
      // Create managers list with user details
      const managersList = allUsers.filter(user => managerIds.has(user.id));
      setManagers(managersList.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')));
      
      // Filter users: active and non-exempt only
      let filteredUsers = allUsers.filter((u) => u.is_active && !u.is_exempt);
      if (selectedManager !== "all") {
        filteredUsers = filteredUsers.filter((u) => {
          const rt = u.reports_to;
          const ids = Array.isArray(rt) ? rt : (rt ? [rt] : []);
          return ids.includes(selectedManager);
        });
      }
      if (selectedTeam !== "all") {
        filteredUsers = filteredUsers.filter((u) => u.team_ids && u.team_ids.includes(selectedTeam));
      }
      // Sort alphabetically by full_name
      filteredUsers.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      setUsers(filteredUsers);
      
      // Auto-expand all teams when data loads
      const teamIds = teamsRes.data.reduce((acc, t) => ({ ...acc, [t.id]: true }), {});
      setExpandedTeams(teamIds);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleNotesCountChange = (logId, newCount) => {
    setNotesCounts(prev => ({ ...prev, [logId]: newCount }));
  };

  // Open PDF Report dialog
  const openPdfReportDialog = () => {
    let formToUse = null;
    
    // If a specific team is selected, find the team leader/manager and use their form
    if (selectedTeam !== "all") {
      // Find manager by reports_to (find who others report to) or team leader role
      const teamMembers = users.filter(u => u.team_ids?.includes(selectedTeam));
      
      // Option 1: Find who team members report to (manager)
      const managerIds = new Set(teamMembers.flatMap(u => {
        const rt = u.reports_to;
        return Array.isArray(rt) ? rt : (rt ? [rt] : []);
      }).filter(Boolean));
      let manager = null;
      
      if (managerIds.size > 0) {
        // Find the manager in users list
        manager = users.find(u => managerIds.has(u.id) && u.assigned_form_id);
      }
      
      // Option 2: Find team leader with assigned form
      if (!manager) {
        manager = teamMembers.find(u => 
          (u.role === "TEAM_LEADER" || u.role === "ADMIN") && 
          u.assigned_form_id
        );
      }
      
      if (manager && manager.assigned_form_id) {
        formToUse = forms.find(f => f.id === manager.assigned_form_id);
      }
    }
    
    // Fallback to default form if no team leader form found
    if (!formToUse) {
      formToUse = forms.find(f => f.is_default) || forms[0];
    }
    
    if (formToUse) {
      setSelectedFormId(formToUse.id);
      // Get all text fields from the form and check them by default
      const textFields = getFormTextFields(formToUse);
      setSelectedFormFields(textFields.map(f => f.id));
    } else {
      // No forms, use default fields
      setSelectedFormId("");
      setSelectedFormFields(["tasks_today", "challenges", "plan_tomorrow", "ai_tools_used"]);
    }
    setCcEmails("");
    setPdfReportDialogOpen(true);
  };

  // Get text fields from a form
  const getFormTextFields = (form) => {
    if (!form || !form.fields) {
      // Default fields if no form
      return [
        { id: "tasks_today", label: "Tasks Worked On Today" },
        { id: "challenges", label: "Challenges and Hurdles" },
        { id: "plan_tomorrow", label: "Plan for Tomorrow" },
        { id: "ai_tools_used", label: "AI Tools Discovery" }
      ];
    }
    // Include ALL field types (text, textarea, rich_text, number, date, etc.)
    // Exclude only file upload fields
    return form.fields
      .filter(f => f.field_type !== "file")
      .map(f => ({ id: f.id, label: f.label }));
  };

  // Handle form selection change
  const handleFormChange = (formId) => {
    setSelectedFormId(formId);
    
    if (!formId || formId === "default") {
      // Use default fields
      setSelectedFormFields(["tasks_today", "challenges", "plan_tomorrow", "ai_tools_used"]);
    } else {
      // Find the selected form
      const form = forms.find(f => f.id === formId);
      if (form) {
        const textFields = getFormTextFields(form);
        setSelectedFormFields(textFields.map(f => f.id));
      }
    }
  };

  // Toggle field selection
  const toggleFieldSelection = (fieldId) => {
    setSelectedFormFields(prev => 
      prev.includes(fieldId) 
        ? prev.filter(id => id !== fieldId)
        : [...prev, fieldId]
    );
  };

  // Send PDF report with selected options
  const handleSendPdfReport = async () => {
    if (selectedFormFields.length === 0) {
      toast.error("Please select at least one field to include in the report");
      return;
    }

    setSendingPdfReport(true);
    try {
      // Parse CC emails
      const ccList = ccEmails
        .split(/[,;\s]+/)
        .map(e => e.trim())
        .filter(e => e && e.includes("@"));

      const payload = { 
        report_date: currentRange.from,
        team_id: selectedTeam !== "all" ? selectedTeam : null,
        form_id: selectedFormId || null,
        selected_fields: selectedFormFields,
        cc_emails: ccList
      };
      
      await axios.post(`${API}/admin/jobs/trigger-report`, payload);
      
      const teamName = selectedTeam !== "all" ? teams.find(t => t.id === selectedTeam)?.name : "All Teams";
      let successMsg = `Report for ${currentRange.from} (${teamName}) sent!`;
      if (ccList.length > 0) {
        successMsg += ` CC: ${ccList.join(", ")}`;
      }
      toast.success(successMsg);
      setPdfReportDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send report");
    } finally {
      setSendingPdfReport(false);
    }
  };

  const handleTriggerReport = async () => {
    setSendingReport(true);
    try {
      const payload = { 
        report_date: currentRange.from,
        team_id: selectedTeam !== "all" ? selectedTeam : null
      };
      await axios.post(`${API}/admin/jobs/trigger-report`, payload);
      const teamName = selectedTeam !== "all" ? teams.find(t => t.id === selectedTeam)?.name : "All Teams";
      toast.success(`Report for ${currentRange.from} (${teamName}) sent to taj@jobtalk.ai!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to trigger report");
    } finally {
      setSendingReport(false);
    }
  };

  const handleSendReminders = async (userIds) => {
    if (!userIds || userIds.length === 0) {
      toast.info("No missing users to send reminders to");
      return;
    }
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/admin/send-reminders`, {
        log_date: currentRange.to, // Send for the latest date in range
        user_ids: userIds
      });
      toast.success(`Reminders sent to ${response.data.sent_count} users!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  // Open reminder dialog for single user or multiple users
  const openReminderDialog = (users) => {
    // Accept either a single user object or an array of users
    const userArray = Array.isArray(users) ? users : [users];
    setReminderUsers(userArray);
    setSelectedReminderUsers(userArray.map(u => u.id)); // All selected by default
    setReminderMessage(DEFAULT_REMINDER_MESSAGE);
    setReminderDialogOpen(true);
  };

  // Toggle user selection in reminder dialog
  const toggleReminderUserSelection = (userId) => {
    setSelectedReminderUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select/deselect all users
  const toggleAllReminderUsers = () => {
    if (selectedReminderUsers.length === reminderUsers.length) {
      setSelectedReminderUsers([]);
    } else {
      setSelectedReminderUsers(reminderUsers.map(u => u.id));
    }
  };

  // Send reminder with custom message from dialog
  const handleSendReminderFromDialog = async () => {
    if (!selectedReminderUsers || selectedReminderUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    
    setSendingReminderDialog(true);
    try {
      const response = await axios.post(`${API}/admin/send-reminders`, {
        log_date: currentRange.to,
        user_ids: selectedReminderUsers,
        custom_message: reminderMessage || null,
        cc_manager: ccManager
      });
      if (response.data.sent_count > 0) {
        const userNames = selectedReminderUsers.length === 1 
          ? reminderUsers.find(u => u.id === selectedReminderUsers[0])?.full_name 
          : `${response.data.sent_count} users`;
        toast.success(`Reminder sent to ${userNames}!`);
        setReminderDialogOpen(false);
        setReminderUsers([]);
        setSelectedReminderUsers([]);
        setReminderMessage("");
        // Refresh reminder stats after sending
        await refreshReminderStats();
      } else {
        toast.info(`Could not send reminders`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingReminderDialog(false);
    }
  };

  const handleSendIndividualReminder = async (userId, userName) => {
    setSendingIndividualReminder(userId);
    try {
      const response = await axios.post(`${API}/admin/send-reminders`, {
        log_date: currentRange.to,
        user_ids: [userId]
      });
      if (response.data.sent_count > 0) {
        toast.success(`Reminder sent to ${userName}!`);
        // Refresh reminder stats after sending
        await refreshReminderStats();
      } else {
        toast.info(`Could not send reminder to ${userName}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingIndividualReminder(null);
    }
  };

  const toggleTeam = (teamId) => {
    setExpandedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  // Strip HTML helper for CSV export
  const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  // Handle print report
  const handlePrintReport = () => {
    const printWindow = window.open('', '_blank');
    const teamName = selectedTeam !== "all" ? teams.find(t => t.id === selectedTeam)?.name : "All Teams";
    const dateRange = currentRange.from === currentRange.to 
      ? format(parseISO(currentRange.from), "MMMM d, yyyy")
      : `${format(parseISO(currentRange.from), "MMM d")} - ${format(parseISO(currentRange.to), "MMM d, yyyy")}`;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Daily Log Report - ${dateRange}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          h1 { font-size: 24px; margin-bottom: 5px; }
          .subtitle { color: #666; margin-bottom: 20px; }
          .stats { display: flex; gap: 20px; margin-bottom: 20px; padding: 15px; background: #f5f5f5; border-radius: 8px; }
          .stat { text-align: center; }
          .stat-value { font-size: 24px; font-weight: bold; }
          .stat-label { font-size: 12px; color: #666; }
          .team-section { margin-bottom: 25px; border: 1px solid #ddd; border-radius: 8px; overflow: hidden; }
          .team-header { background: #f5f5f5; padding: 12px 15px; font-weight: bold; border-bottom: 1px solid #ddd; }
          .log-entry { padding: 15px; border-bottom: 1px solid #eee; }
          .log-entry:last-child { border-bottom: none; }
          .log-header { display: flex; justify-content: space-between; margin-bottom: 10px; }
          .user-name { font-weight: bold; }
          .log-date { color: #666; font-size: 14px; }
          .log-section { margin-bottom: 12px; }
          .log-section-title { font-size: 12px; color: #666; text-transform: uppercase; margin-bottom: 5px; }
          .log-section-content { background: #f9f9f9; padding: 10px; border-radius: 4px; font-size: 14px; }
          .badge { display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 12px; }
          .badge-green { background: #dcfce7; color: #166534; }
          .badge-amber { background: #fef3c7; color: #92400e; }
          .metadata { font-size: 11px; color: #999; margin-top: 10px; padding-top: 10px; border-top: 1px solid #eee; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>Daily Log Report</h1>
        <div class="subtitle">${teamName} • ${dateRange}</div>
        
        <div class="stats">
          <div class="stat">
            <div class="stat-value">${totalLogs}</div>
            <div class="stat-label">Total Logs</div>
          </div>
          <div class="stat">
            <div class="stat-value">${totalUsers}</div>
            <div class="stat-label">Total Users</div>
          </div>
          <div class="stat">
            <div class="stat-value">${fullySubmittedUsers.length}</div>
            <div class="stat-label">${isMultiDay ? 'Fully Submitted' : 'Submitted'}</div>
          </div>
          ${isMultiDay ? `<div class="stat">
            <div class="stat-value">${partialUsers.length}</div>
            <div class="stat-label">Partial</div>
          </div>` : ''}
          <div class="stat">
            <div class="stat-value">${isMultiDay ? fullyMissingUsers.length : missingCount}</div>
            <div class="stat-label">${isMultiDay ? 'Fully Missing' : 'Missing'}</div>
          </div>
        </div>

        ${Object.values(teamMap).filter(t => t.totalLogs > 0).map(({ team, users: teamUsers }) => `
          <div class="team-section">
            <div class="team-header">${team.name}</div>
            ${Object.values(teamUsers).map(({ user_name, user_email, logs: userLogs }) => 
              userLogs.map(log => `
                <div class="log-entry">
                  <div class="log-header">
                    <div>
                      <span class="user-name">${log.user_name}</span>
                      <span style="color: #666; font-size: 12px;"> (${log.user_email})</span>
                    </div>
                    <div>
                      <span class="log-date">${format(parseISO(log.log_date), "MMM d, yyyy")}</span>
                      ${log.day_type 
                        ? `<span class="badge badge-amber" style="margin-left: 10px;">${log.day_type.toUpperCase().replace('_', ' ')}</span>`
                        : '<span class="badge badge-green" style="margin-left: 10px;">Submitted</span>'
                      }
                    </div>
                  </div>
                  ${!log.day_type ? (() => {
                    const form = forms.find(frm => frm.id === log.form_id) || forms.find(frm => frm.id === log.assigned_form_id);
                    const fields = form?.fields?.filter(fld => fld.field_type !== 'file') || [
                      { id: 'tasks_today', label: 'Tasks Worked On Today' },
                      { id: 'challenges', label: 'Challenges and Hurdles' },
                      { id: 'plan_tomorrow', label: 'Plan for Tomorrow' },
                    ];
                    return fields.map(fld => {
                      const val = log[fld.id];
                      const display = val === 0 ? '0' : (val || '-');
                      return '<div class="log-section"><div class="log-section-title">' + fld.label + '</div><div class="log-section-content">' + display + '</div></div>';
                    }).join('');
                  })() : `
                    <div style="text-align: center; padding: 20px; background: #fef3c7; border-radius: 8px;">
                      <strong style="font-size: 18px; color: #92400e;">${log.day_type.toUpperCase().replace('_', ' ')}</strong>
                    </div>
                  `}
                  ${jdData[log.user_id] ? (() => {
                    const jd = jdData[log.user_id];
                    const selfSub = isMultiDay ? (userLogTotals[log.user_id]?.submittals ?? 0) : parseInt(log.num_submittals || 0);
                    const selfInt = isMultiDay ? (userLogTotals[log.user_id]?.interviews ?? 0) : parseInt(log.num_interviews || 0);
                    const hasMismatch = selfSub !== jd.submittals || selfInt !== jd.interviews;
                    return '<div style="background:' + (hasMismatch ? '#fffbeb;border:1px solid #fcd34d' : '#eef2ff;border:1px solid #c7d2fe') + ';border-radius:8px;padding:10px;margin-top:10px;">'
                      + '<div style="font-size:11px;font-weight:bold;color:#4338ca;margin-bottom:5px;">JobDiva ATS vs Self-Reported' + (isMultiDay ? ' (Range)' : '') + (hasMismatch ? ' <span style="color:#b45309;background:#fef3c7;padding:2px 6px;border-radius:8px;font-size:10px;">Mismatch</span>' : '') + '</div>'
                      + '<div style="display:flex;gap:20px;">'
                      + '<span><strong style="' + (selfSub !== jd.submittals ? 'color:#b45309' : '') + '">' + jd.submittals + '</strong> JD Sub' + (selfSub !== jd.submittals ? ' <small style="color:#666">(Self: ' + selfSub + ')</small>' : '') + '</span>'
                      + '<span><strong style="' + (selfInt !== jd.interviews ? 'color:#b45309' : '') + '">' + jd.interviews + '</strong> JD Int' + (selfInt !== jd.interviews ? ' <small style="color:#666">(Self: ' + selfInt + ')</small>' : '') + '</span>'
                      + '<span><strong>' + jd.hires + '</strong> JD Hire</span>'
                      + '</div></div>';
                  })() : ''}
                  <div class="metadata">
                    Submitted: ${formatDateEST(log.created_at)} • ${log.browser_name} / ${log.os_name} • ${[log.geo_city, log.geo_region, log.geo_country].filter(Boolean).join(", ") || log.ip_address}
                  </div>
                </div>
              `).join('')
            ).join('')}
          </div>
        `).join('')}

        ${missingUsers.length > 0 ? `
          <div class="team-section">
            <div class="team-header" style="background: #fef2f2; color: #dc2626;">Missing Submissions (${missingUsers.length})</div>
            <div style="padding: 15px;">
              ${missingUsers.map(u => `<div style="padding: 5px 0;">
                <strong>${u.full_name}</strong> (${u.email})
                ${isMultiDay ? `<br><span style="color: #dc2626; font-size: 12px;">${u.submittedDays}/${u.totalDays} days — missing: ${u.missingDates.map(d => format(parseISO(d), "EEE MMM d")).join(", ")}</span>` : ''}
              </div>`).join('')}
            </div>
          </div>
        ` : ''}

        <div style="margin-top: 20px; text-align: center; color: #999; font-size: 12px;">
          Generated on ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Export to CSV
  const handleExportCsv = () => {
    setExportingCsv(true);
    try {
      const teamName = selectedTeam !== "all" ? teams.find(t => t.id === selectedTeam)?.name : "All Teams";
      const dateRange = currentRange.from === currentRange.to 
        ? format(parseISO(currentRange.from), "yyyy-MM-dd")
        : `${currentRange.from}_to_${currentRange.to}`;
      
      // Build dynamic field headers from forms
      const getLogFormFields = (log) => {
        const form = forms.find(f => f.id === log.form_id || f.id === log.assigned_form_id);
        return form?.fields?.filter(f => f.field_type !== 'file') || [
          { id: 'tasks_today', label: 'Tasks Today' },
          { id: 'challenges', label: 'Challenges' },
          { id: 'plan_tomorrow', label: 'Plan Tomorrow' },
        ];
      };
      // Use first log's form for column headers (all logs in same view likely share a form)
      const csvFormFields = logs.length > 0 ? getLogFormFields(logs[0]) : [
        { id: 'tasks_today', label: 'Tasks Today' },
        { id: 'challenges', label: 'Challenges' },
        { id: 'plan_tomorrow', label: 'Plan Tomorrow' },
      ];
      const jdHeaders = Object.keys(jdData).length > 0 ? ["JD Submittals", "JD Interviews", "JD Hires"] : [];
      const headers = ["Date", "Team", "User Name", "Email", "Status", "Day Type", ...csvFormFields.map(f => f.label), ...jdHeaders, "Submitted At", "Location"];
      const rows = logs.map(log => {
        const userTeamNames = (log.team_ids || []).map(tid => teams.find(t => t.id === tid)?.name || "Unknown").join("; ");
        return [
          log.log_date,
          userTeamNames || "No Team",
          `"${log.user_name}"`,
          log.user_email,
          log.day_type ? "Non-Work Day" : "Submitted",
          log.day_type || "",
          ...csvFormFields.map(f => `"${stripHtml(log[f.id] || "").replace(/"/g, '""')}"`),
          ...(jdHeaders.length > 0 ? [jdData[log.user_id]?.submittals ?? '', jdData[log.user_id]?.interviews ?? '', jdData[log.user_id]?.hires ?? ''] : []),
          formatDateEST(log.created_at),
          `"${[log.geo_city, log.geo_region, log.geo_country].filter(Boolean).join(", ") || log.ip_address}"`
        ];
      });

      // Add missing users with per-day info for multi-day
      missingUsers.forEach(user => {
        const userTeamNames = (user.team_ids || []).map(tid => teams.find(t => t.id === tid)?.name || "Unknown").join("; ");
        const missingInfo = isMultiDay ? `Missing ${user.missingDates.length}/${user.totalDays} days: ${user.missingDates.join(", ")}` : "";
        rows.push([
          isMultiDay ? missingInfo : currentRange.to,
          userTeamNames || "No Team",
          `"${user.full_name}"`,
          user.email,
          "Missing",
          "",
          ...csvFormFields.map(() => ""),
          ...(jdHeaders.length > 0 ? ['', '', ''] : []),
          "",
          ""
        ]);
      });

      const statsLine = isMultiDay
        ? `Total Logs: ${totalLogs} | Fully Submitted: ${fullySubmittedUsers.length} | Partial: ${partialUsers.length} | Fully Missing: ${fullyMissingUsers.length}`
        : `Total Logs: ${totalLogs} | Submitted: ${fullySubmittedUsers.length} | Missing: ${missingCount}`;
      const csvContent = [
        `Daily Log Report - ${teamName}`,
        `Period: ${currentRange.from} to ${currentRange.to}`,
        `Generated: ${format(new Date(), "yyyy-MM-dd HH:mm")}`,
        statsLine,
        "",
        headers.join(","),
        ...rows.map(row => row.join(","))
      ].join("\n");
      
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `daily_log_report_${teamName.replace(/\s+/g, "_")}_${dateRange}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      
      toast.success("CSV exported successfully");
    } catch (err) {
      console.error("CSV export error:", err);
      toast.error("Failed to export CSV");
    } finally {
      setExportingCsv(false);
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    setExportingExcel(true);
    try {
      const teamName = selectedTeam !== "all" ? teams.find(t => t.id === selectedTeam)?.name : "All Teams";
      const dateRange = currentRange.from === currentRange.to 
        ? format(parseISO(currentRange.from), "yyyy-MM-dd")
        : `${currentRange.from}_to_${currentRange.to}`;
      
      // Build dynamic fields from forms
      const xlFormFields = logs.length > 0 ? (() => {
        const form = forms.find(f => f.id === logs[0].form_id || f.id === logs[0].assigned_form_id);
        return form?.fields?.filter(f => f.field_type !== 'file') || [
          { id: 'tasks_today', label: 'Tasks Today' },
          { id: 'challenges', label: 'Challenges' },
          { id: 'plan_tomorrow', label: 'Plan Tomorrow' },
        ];
      })() : [{ id: 'tasks_today', label: 'Tasks Today' }, { id: 'challenges', label: 'Challenges' }, { id: 'plan_tomorrow', label: 'Plan Tomorrow' }];

      const excelData = logs.map(log => {
        const userTeamNames = (log.team_ids || []).map(tid => teams.find(t => t.id === tid)?.name || "Unknown").join("; ");
        const row = {
          "Date": log.log_date,
          "Team": userTeamNames || "No Team",
          "User Name": log.user_name,
          "Email": log.user_email,
          "Status": log.day_type ? "Non-Work Day" : "Submitted",
          "Day Type": log.day_type || "",
        };
        xlFormFields.forEach(f => { row[f.label] = stripHtml(log[f.id] || ""); });
        if (Object.keys(jdData).length > 0) {
          row["JD Submittals"] = jdData[log.user_id]?.submittals ?? '';
          row["JD Interviews"] = jdData[log.user_id]?.interviews ?? '';
          row["JD Hires"] = jdData[log.user_id]?.hires ?? '';
        }
        row["Submitted At"] = formatDateEST(log.created_at);
        row["Location"] = [log.geo_city, log.geo_region, log.geo_country].filter(Boolean).join(", ") || log.ip_address;
        return row;
      });

      // Add missing users
      missingUsers.forEach(user => {
        const userTeamNames = (user.team_ids || []).map(tid => teams.find(t => t.id === tid)?.name || "Unknown").join("; ");
        const row = {
          "Date": isMultiDay ? `Missing ${user.missingDates.length}/${user.totalDays} days` : currentRange.to,
          "Team": userTeamNames || "No Team",
          "User Name": user.full_name,
          "Email": user.email,
          "Status": "Missing",
          "Day Type": isMultiDay ? user.missingDates.join(", ") : "",
        };
        xlFormFields.forEach(f => { row[f.label] = ""; });
        if (Object.keys(jdData).length > 0) {
          row["JD Submittals"] = ''; row["JD Interviews"] = ''; row["JD Hires"] = '';
        }
        row["Submitted At"] = "";
        row["Location"] = "";
        excelData.push(row);
      });
      
      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths dynamically
      worksheet['!cols'] = [
        { wch: 12 },  // Date
        { wch: 20 },  // Team
        { wch: 20 },  // User Name
        { wch: 25 },  // Email
        { wch: 15 },  // Status
        { wch: 12 },  // Day Type
        ...xlFormFields.map(() => ({ wch: 50 })),  // Dynamic form fields
        { wch: 25 },  // Submitted At
        { wch: 30 },  // Location
      ];
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Daily Logs");
      
      // Add summary sheet
      const summaryData = [
        { "Metric": "Report Period", "Value": `${currentRange.from} to ${currentRange.to}` },
        { "Metric": "Team Filter", "Value": teamName },
        { "Metric": "Total Logs", "Value": totalLogs },
        { "Metric": "Total Users", "Value": totalUsers },
        { "Metric": "Fully Submitted", "Value": fullySubmittedUsers.length },
        ...(isMultiDay ? [{ "Metric": "Partially Submitted", "Value": partialUsers.length }] : []),
        { "Metric": "Missing", "Value": isMultiDay ? fullyMissingUsers.length : missingCount },
        { "Metric": "Generated On", "Value": format(new Date(), "yyyy-MM-dd HH:mm") },
      ];
      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      summarySheet['!cols'] = [{ wch: 20 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
      
      // Download
      XLSX.writeFile(workbook, `daily_log_report_${teamName.replace(/\s+/g, "_")}_${dateRange}.xlsx`);
      
      toast.success("Excel file exported successfully");
    } catch (err) {
      console.error("Excel export error:", err);
      toast.error("Failed to export Excel");
    } finally {
      setExportingExcel(false);
    }
  };

  // Filter logs based on search query and productivity filter
  let filteredLogs = searchQuery.trim()
    ? logs.filter(log =>
        log.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : logs;

  if (productivityFilter !== "") {
    const threshold = parseInt(productivityFilter, 10);
    if (!isNaN(threshold)) {
      filteredLogs = filteredLogs.filter(log => {
        if (!log.productivity_rating) return false;
        const rating = parseFloat(log.productivity_rating);
        return !isNaN(rating) && rating <= threshold;
      });
    }
  }

  // Group logs by team and user
  const groupedData = () => {
    const teamMap = {};
    const noTeamLogs = [];
    
    // Initialize teams
    teams.forEach(team => {
      teamMap[team.id] = {
        team,
        users: {},
        totalLogs: 0,
        userCount: 0
      };
    });
    
    // Group filtered logs
    filteredLogs.forEach(log => {
      const userTeamIds = log.team_ids || [];
      
      if (userTeamIds.length === 0) {
        // User has no team - add all their logs
        noTeamLogs.push(log);
      } else {
        userTeamIds.forEach(teamId => {
          if (teamMap[teamId]) {
            if (!teamMap[teamId].users[log.user_id]) {
              teamMap[teamId].users[log.user_id] = {
                user_id: log.user_id,
                user_name: log.user_name,
                user_email: log.user_email,
                logs: []
              };
              teamMap[teamId].userCount++;
            }
            teamMap[teamId].users[log.user_id].logs.push(log);
            teamMap[teamId].totalLogs++;
          }
        });
      }
    });
    
    return { teamMap, noTeamLogs };
  };

  const { teamMap, noTeamLogs } = groupedData();
  
  // Calculate working days in the range
  const getWorkingDays = (fromStr, toStr) => {
    const days = [];
    const from = parseISO(fromStr);
    const to = parseISO(toStr);
    const today = new Date();
    let d = new Date(from);
    while (d <= to && d <= today) {
      if (d.getDay() !== 0 && d.getDay() !== 6) {
        days.push(format(d, "yyyy-MM-dd"));
      }
      d = new Date(d.getTime() + 86400000);
    }
    return days;
  };

  const workingDays = getWorkingDays(currentRange.from, currentRange.to);
  const isMultiDay = workingDays.length > 1;

  // Calculate stats
  const totalLogs = logs.length;
  const uniqueUserIds = new Set(logs.map(l => l.user_id));
  const usersWithLogs = uniqueUserIds.size;
  const totalUsers = users.length;

  // Build per-user submission map: user_id -> Set of dates they submitted
  const userLogDates = {};
  logs.forEach(l => {
    if (!userLogDates[l.user_id]) userLogDates[l.user_id] = new Set();
    userLogDates[l.user_id].add(l.log_date);
  });

  // Aggregate self-reported totals per user (for multi-day JD comparison)
  const userLogTotals = {};
  if (isMultiDay) {
    logs.forEach(l => {
      if (!userLogTotals[l.user_id]) userLogTotals[l.user_id] = { submittals: 0, interviews: 0 };
      userLogTotals[l.user_id].submittals += parseInt(l.num_submittals || 0);
      userLogTotals[l.user_id].interviews += parseInt(l.num_interviews || 0);
    });
  }

  // Categorize users: fully submitted, partial, fully missing
  const allUserStats = users.map(u => {
    const submitted = userLogDates[u.id] || new Set();
    const missingDates = workingDays.filter(d => !submitted.has(d));
    return { ...u, missingDates, submittedDays: submitted.size, totalDays: workingDays.length };
  });

  const fullySubmittedUsers = allUserStats.filter(u => u.missingDates.length === 0);
  const partialUsers = allUserStats.filter(u => u.missingDates.length > 0 && u.submittedDays > 0);
  const fullyMissingUsers = allUserStats.filter(u => u.submittedDays === 0);
  const missingUsers = allUserStats.filter(u => u.missingDates.length > 0); // partial + fully missing
  const missingCount = missingUsers.length;

  // Fetch reminder stats for missing users
  useEffect(() => {
    const fetchReminderStats = async () => {
      if (missingUsers.length === 0) {
        setReminderStats({});
        return;
      }
      
      try {
        const emails = missingUsers.map(u => u.email).join(",");
        const response = await axios.get(`${API}/admin/reminder-stats`, {
          params: { user_emails: emails, log_date: currentRange.to }
        });
        setReminderStats(response.data.stats || {});
      } catch (error) {
        console.error("Failed to fetch reminder stats:", error);
      }
    };
    
    fetchReminderStats();
  }, [missingUsers.length, currentRange.to]);

  // Refresh reminder stats after sending
  const refreshReminderStats = async () => {
    if (missingUsers.length === 0) return;
    
    try {
      const emails = missingUsers.map(u => u.email).join(",");
      const response = await axios.get(`${API}/admin/reminder-stats`, {
        params: { user_emails: emails, log_date: currentRange.to }
      });
      setReminderStats(response.data.stats || {});
    } catch (error) {
      console.error("Failed to refresh reminder stats:", error);
    }
  };

  const handleGenerateManagerReports = async () => {
    setGeneratingManagerReports(true);
    try {
      const response = await axios.post(`${API}/admin/generate-manager-reports`, {
        start_date: currentRange.from,
        end_date: currentRange.to,
        action: managerReportsOption  // "download" or "email"
      });
      
      if (managerReportsOption === "download") {
        // Decode base64 ZIP and trigger download
        const zipData = atob(response.data.zip_data);
        const bytes = new Uint8Array(zipData.length);
        for (let i = 0; i < zipData.length; i++) {
          bytes[i] = zipData.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename;
        link.click();
        URL.revokeObjectURL(url);
        toast.success(`Generated ${response.data.manager_count} manager reports. Download started.`);
      } else {
        // Emails sent
        toast.success(`Sent ${response.data.emails_sent} manager reports via email`);
      }
      
      setManagerReportsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate manager reports');
    } finally {
      setGeneratingManagerReports(false);
    }
  };

  const handleGenerateMtr = async () => {
    setGeneratingMtr(true);
    try {
      const response = await axios.post(`${API}/admin/generate-mtr`, {
        start_date: currentRange.from,
        end_date: currentRange.to,
        action: mtrOption
      });

      if (mtrOption === "download") {
        const zipData = atob(response.data.zip_data);
        const bytes = new Uint8Array(zipData.length);
        for (let i = 0; i < zipData.length; i++) {
          bytes[i] = zipData.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/zip' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename;
        link.click();
        URL.revokeObjectURL(url);
        toast.success(`Generated ${response.data.manager_count} MTR reports. Download started.`);
      } else {
        toast.success(`Sent ${response.data.emails_sent} MTR reports via email`);
      }

      setMtrDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate MTR reports');
    } finally {
      setGeneratingMtr(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Reports
          </h1>
          <p className="text-slate-600 mt-1">
            View logs grouped by teams and users
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={handleExportCsv} 
            disabled={loading || logs.length === 0 || exportingCsv}
            data-testid="main-export-csv-btn"
          >
            {exportingCsv ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}
            CSV
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportExcel} 
            disabled={loading || logs.length === 0 || exportingExcel}
            data-testid="main-export-excel-btn"
          >
            {exportingExcel ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Table className="mr-2 h-4 w-4" />}
            Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setReportDialogOpen(true)} 
            disabled={loading || logs.length === 0}
            data-testid="display-report-button"
          >
            <Eye className="mr-2 h-4 w-4" />
            Display Report
          </Button>
          <Button 
            variant="outline"
            onClick={() => setManagerReportsDialogOpen(true)}
            disabled={loading}
            className="bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
            data-testid="manager-reports-btn"
          >
            <Users className="mr-2 h-4 w-4" />
            Mgr. Reports
          </Button>
          <Button
            variant="outline"
            onClick={() => setMtrDialogOpen(true)}
            disabled={loading}
            className="bg-indigo-50 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
            data-testid="mtr-btn"
          >
            <ClipboardList className="mr-2 h-4 w-4" />
            MTR
          </Button>
          <Button onClick={openPdfReportDialog} disabled={sendingReport} data-testid="trigger-report-button">
            {sendingReport ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Email PDF Report
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="w-[180px]">
          <Select value={selectedRange} onValueChange={setSelectedRange}>
            <SelectTrigger data-testid="report-range-selector">
              <Calendar className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Select range" />
            </SelectTrigger>
            <SelectContent>
              {dateRangePresets.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[180px]">
          <Select value={selectedTeam} onValueChange={(val) => { setSelectedTeam(val); setSelectedUser("all"); }}>
            <SelectTrigger data-testid="report-team-selector">
              <Building2 className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filter by team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[220px]">
          <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={userSearchOpen}
                className="w-full justify-between font-normal"
                data-testid="report-user-selector"
              >
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2 text-slate-400" />
                  {selectedUser === "all" 
                    ? "All Users" 
                    : users.find((u) => u.id === selectedUser)?.full_name || "Select user"}
                </div>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0">
              <Command>
                <CommandInput placeholder="Search users..." />
                <CommandList>
                  <CommandEmpty>No user found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => { setSelectedUser("all"); setUserSearchOpen(false); }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", selectedUser === "all" ? "opacity-100" : "opacity-0")} />
                      All Users
                    </CommandItem>
                    {users.map((user) => (
                      <CommandItem
                        key={user.id}
                        value={user.full_name}
                        onSelect={() => { setSelectedUser(user.id); setUserSearchOpen(false); }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", selectedUser === user.id ? "opacity-100" : "opacity-0")} />
                        {user.full_name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="w-[220px]">
          <Popover open={managerSearchOpen} onOpenChange={setManagerSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={managerSearchOpen}
                className="w-full justify-between font-normal"
                data-testid="report-manager-selector"
              >
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-2 text-slate-400" />
                  {selectedManager === "all" 
                    ? "All Managers" 
                    : managers.find((u) => u.id === selectedManager)?.full_name || "Select manager"}
                </div>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0">
              <Command>
                <CommandInput placeholder="Search managers..." />
                <CommandList>
                  <CommandEmpty>No manager found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => { setSelectedManager("all"); setManagerSearchOpen(false); }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", selectedManager === "all" ? "opacity-100" : "opacity-0")} />
                      All Managers
                    </CommandItem>
                    {managers.map((manager) => (
                      <CommandItem
                        key={manager.id}
                        value={manager.full_name}
                        onSelect={() => { setSelectedManager(manager.id); setManagerSearchOpen(false); }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", selectedManager === manager.id ? "opacity-100" : "opacity-0")} />
                        {manager.full_name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        
        {/* Date range display */}
        <div className="flex items-center text-sm text-slate-500 bg-slate-100 px-3 py-2 rounded-md">
          <Calendar className="h-4 w-4 mr-2" />
          {currentRange.from === currentRange.to 
            ? format(parseISO(currentRange.from), "MMM d, yyyy")
            : `${format(parseISO(currentRange.from), "MMM d")} - ${format(parseISO(currentRange.to), "MMM d, yyyy")}`
          }
        </div>
        
        {/* Productivity filter */}
        <div className="w-[80px] relative">
          <div className="relative">
            <Input
              type="number"
              min="1"
              max="10"
              placeholder="P ≤"
              value={productivityFilter}
              onChange={(e) => setProductivityFilter(e.target.value)}
              className="text-center"
            />
            {productivityFilter && (
              <button
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setProductivityFilter("")}
              >
                <XCircle className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Search by name */}
        <div className="w-[300px] relative" ref={searchRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
              className="pl-9"
              data-testid="search-by-name-input"
            />
            {searchQuery && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setSearchQuery("")}
              >
                <XCircle className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Search suggestions dropdown */}
          {searchFocused && searchQuery.trim() && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-[300px] overflow-y-auto">
              {users.filter(u => 
                u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                u.email?.toLowerCase().includes(searchQuery.toLowerCase())
              ).length > 0 ? (
                users.filter(u => 
                  u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  u.email?.toLowerCase().includes(searchQuery.toLowerCase())
                ).slice(0, 10).map(user => {
                  const hasLog = logs.some(l => l.user_id === user.id);
                  return (
                    <div
                      key={user.id}
                      className={cn(
                        "px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-0",
                        hasLog ? "bg-green-50/50" : ""
                      )}
                      onClick={() => {
                        setSelectedUser(user.id);
                        setSearchQuery(user.full_name);
                        setSearchFocused(false);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{user.full_name}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                        {hasLog ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Submitted
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs">
                            <XCircle className="h-3 w-3 mr-1" />
                            Missing
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-3 py-4 text-center text-slate-500 text-sm">
                  No users found matching &quot;{searchQuery}&quot;
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className={`grid grid-cols-1 ${isMultiDay ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4`}>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalLogs}</p>
                <p className="text-sm text-slate-500">Total Logs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalUsers}</p>
                <p className="text-sm text-slate-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Fully Submitted */}
        <HoverCard openDelay={100} closeDelay={100}>
          <HoverCardTrigger asChild>
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{fullySubmittedUsers.length}</p>
                    <p className="text-sm text-slate-500">{isMultiDay ? 'Fully Submitted' : 'Users Submitted'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </HoverCardTrigger>
          <HoverCardContent className="w-80 max-h-[300px] overflow-y-auto">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="font-semibold">{isMultiDay ? `Fully Submitted — All ${workingDays.length} Days` : `Users Who Submitted`} ({fullySubmittedUsers.length})</span>
            </div>
            {fullySubmittedUsers.length > 0 ? (
              <div className="space-y-2">
                {fullySubmittedUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-sm">
                    <div>
                      <p className="font-medium text-slate-800">{u.full_name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </div>
                    {isMultiDay && <Badge className="bg-green-100 text-green-700 text-xs hover:bg-green-100">{u.submittedDays}/{u.totalDays}</Badge>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-2 text-sm">No users submitted all days</p>
            )}
          </HoverCardContent>
        </HoverCard>

        {/* Partial — only show for multi-day */}
        {isMultiDay && (
          <HoverCard openDelay={100} closeDelay={100}>
            <HoverCardTrigger asChild>
              <Card className="cursor-pointer hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Clock className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{partialUsers.length}</p>
                      <p className="text-sm text-slate-500">Partially Submitted</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 max-h-[300px] overflow-y-auto">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                <Clock className="h-4 w-4 text-amber-600" />
                <span className="font-semibold">Partially Submitted ({partialUsers.length})</span>
              </div>
              {partialUsers.length > 0 ? (
                <div className="space-y-2">
                  {partialUsers.map(u => (
                    <div key={u.id} className="p-2 bg-amber-50 rounded-lg text-sm">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-slate-800">{u.full_name}</p>
                        <Badge className="bg-amber-100 text-amber-700 text-xs hover:bg-amber-100">{u.submittedDays}/{u.totalDays}</Badge>
                      </div>
                      <p className="text-xs text-red-600 mt-0.5">Missing: {u.missingDates.map(d => format(parseISO(d), "EEE d")).join(", ")}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-2 text-sm">No partially submitted users</p>
              )}
            </HoverCardContent>
          </HoverCard>
        )}
        <HoverCard openDelay={100} closeDelay={100}>
          <HoverCardTrigger asChild>
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{isMultiDay ? fullyMissingUsers.length : missingCount}</p>
                    <p className="text-sm text-slate-500">{isMultiDay ? 'Fully Missing' : 'Missing Logs'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </HoverCardTrigger>
          <HoverCardContent className="w-80 max-h-[350px] overflow-y-auto">
            <div className="flex items-center justify-between mb-3 pb-2 border-b">
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-600" />
                <span className="font-semibold">{isMultiDay ? `Fully Missing (${fullyMissingUsers.length})` : `Missing Logs (${missingCount})`}</span>
              </div>
              {missingUsers.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  onClick={() => openReminderDialog(missingUsers)}
                  title="Send reminder to all"
                >
                  <Mail className="h-4 w-4" />
                </Button>
              )}
            </div>
            {(isMultiDay ? fullyMissingUsers : missingUsers).length > 0 ? (
              <div className="space-y-2">
                {(isMultiDay ? fullyMissingUsers : missingUsers).map((user) => {
                  const userStats = reminderStats[user.email] || { count: 0, last_sent: null };
                  const hasReminders = userStats.count > 0;

                  return (
                    <div key={user.id} className="flex items-center justify-between p-2 bg-red-50 rounded-lg text-sm">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{user.full_name}</p>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        {/* Reminder Count Badge with Tooltip */}
                        {hasReminders && (
                          <HoverCard openDelay={0} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <Badge 
                                variant="outline" 
                                className="bg-amber-50 text-amber-700 border-amber-300 cursor-help text-xs px-1.5 py-0"
                              >
                                {userStats.count}
                              </Badge>
                            </HoverCardTrigger>
                            <HoverCardContent side="left" className="w-56 p-2 text-xs">
                              <p className="font-semibold text-slate-700">
                                {userStats.count} reminder{userStats.count !== 1 ? 's' : ''} sent
                              </p>
                              {userStats.last_sent && (
                                <p className="text-slate-500 mt-1">
                                  Last sent: {format(new Date(userStats.last_sent), "MMM d, yyyy 'at' h:mm a")}
                                </p>
                              )}
                            </HoverCardContent>
                          </HoverCard>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            openReminderDialog(user);
                          }}
                          title={`Send reminder to ${user.full_name}`}
                          data-testid={`remind-user-${user.id}`}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                        <XCircle className="h-4 w-4 text-red-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-2 text-sm">All users have submitted! 🎉</p>
            )}
          </HoverCardContent>
        </HoverCard>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No logs found</h3>
            <p className="text-slate-500 mt-1">No logs have been submitted for this period</p>
          </CardContent>
        </Card>
      ) : filteredLogs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <User className="h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No matching users found</h3>
            <p className="text-slate-500 mt-1">No users match &quot;{searchQuery}&quot;</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setSearchQuery("")}
            >
              Clear Search
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Grouped by Teams */}
          {Object.values(teamMap).filter(t => t.totalLogs > 0).map(({ team, users: teamUsers, totalLogs: teamLogs }) => (
            <Card key={team.id} className="overflow-hidden">
              <CardHeader 
                className="bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => toggleTeam(team.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {expandedTeams[team.id] ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
                    <Building2 className="h-5 w-5 text-primary" />
                    <div>
                      <CardTitle className="text-lg">{team.name}</CardTitle>
                      <CardDescription>{Object.keys(teamUsers).length} users • {teamLogs} logs</CardDescription>
                    </div>
                  </div>
                  <Badge variant="secondary">{teamLogs} logs</Badge>
                </div>
              </CardHeader>
              
              {expandedTeams[team.id] && (
                <CardContent className="pt-4 space-y-4">
                  {Object.values(teamUsers).map(({ user_id, user_name, user_email, logs: userLogs }) => (
                    <div key={user_id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3 pb-2 border-b">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <span className="font-medium">{user_name}</span>
                          <span className="text-xs text-slate-500">({user_email})</span>
                        </div>
                        <Badge variant="outline">{userLogs.length} {userLogs.length === 1 ? 'log' : 'logs'}</Badge>
                      </div>
                      <div className="space-y-2">
                        {userLogs.map(log => (
                          <LogCard
                            key={log.id}
                            log={log}
                            teams={teams}
                            forms={forms}
                            users={users}
                            jdData={jdData}
                            isMultiDay={isMultiDay}
                            userLogTotals={userLogTotals}
                            notesCount={notesCounts[log.id] || 0}
                            onNotesChange={handleNotesCountChange}
                          />
                        ))}
                      </div>
                      {/* JD summary after all user's logs — multi-day */}
                      {isMultiDay && jdData[user_id] && (() => {
                        const jd = jdData[user_id];
                        const selfSub = userLogTotals[user_id]?.submittals ?? 0;
                        const selfInt = userLogTotals[user_id]?.interviews ?? 0;
                        const hasMismatch = selfSub !== jd.submittals || selfInt !== jd.interviews;
                        return (
                          <div className={`mt-3 rounded-lg p-3 border ${hasMismatch ? 'bg-amber-50 border-amber-300' : 'bg-indigo-50 border-indigo-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-indigo-700">
                                JobDiva ATS vs Self-Reported (Range Total)
                              </p>
                              {hasMismatch && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Mismatch</span>}
                            </div>
                            <div className="grid grid-cols-3 gap-3 text-center">
                              <div>
                                <p className={`text-lg font-bold ${selfSub !== jd.submittals ? 'text-amber-700' : 'text-indigo-900'}`}>{jd.submittals}</p>
                                <p className="text-[10px] text-indigo-600 uppercase">JD Submittals</p>
                                {selfSub !== jd.submittals && <p className="text-[10px] text-slate-500">Self: {selfSub}</p>}
                              </div>
                              <div>
                                <p className={`text-lg font-bold ${selfInt !== jd.interviews ? 'text-amber-700' : 'text-indigo-900'}`}>{jd.interviews}</p>
                                <p className="text-[10px] text-indigo-600 uppercase">JD Interviews</p>
                                {selfInt !== jd.interviews && <p className="text-[10px] text-slate-500">Self: {selfInt}</p>}
                              </div>
                              <div>
                                <p className="text-lg font-bold text-indigo-900">{jd.hires}</p>
                                <p className="text-[10px] text-indigo-600 uppercase">JD Hires</p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          ))}
          
          {/* Users without team */}
          {noTeamLogs.length > 0 && (
            <Card className="overflow-hidden">
              <CardHeader className="bg-slate-50">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-slate-400" />
                  <div>
                    <CardTitle className="text-lg">No Team Assigned</CardTitle>
                    <CardDescription>{noTeamLogs.length} logs from users without team</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-2">
                {noTeamLogs.map(log => (
                  <LogCard
                    key={log.id}
                    log={log}
                    teams={teams}
                    forms={forms}
                    users={users}
                    jdData={jdData}
                    isMultiDay={isMultiDay}
                    userLogTotals={userLogTotals}
                    notesCount={notesCounts[log.id] || 0}
                    onNotesChange={handleNotesCountChange}
                  />
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Report Display Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl">
                Daily Log Report
              </DialogTitle>
              <div className="flex items-center gap-2 mr-8">
                <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={exportingCsv} data-testid="export-csv-btn">
                  {exportingCsv ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                  )}
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={exportingExcel} data-testid="export-excel-btn">
                  {exportingExcel ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Table className="mr-2 h-4 w-4" />
                  )}
                  Excel
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrintReport} data-testid="print-report-btn">
                  <Printer className="mr-2 h-4 w-4" />
                  Print
                </Button>
              </div>
            </div>
            <p className="text-sm text-slate-500">
              {selectedTeam !== "all" ? teams.find(t => t.id === selectedTeam)?.name : "All Teams"} • {currentRange.from === currentRange.to 
                ? format(parseISO(currentRange.from), "MMMM d, yyyy")
                : `${format(parseISO(currentRange.from), "MMM d")} - ${format(parseISO(currentRange.to), "MMM d, yyyy")}`
              }
            </p>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2" ref={reportRef}>
            {/* Stats Summary */}
            <div className={`grid ${isMultiDay ? 'grid-cols-5' : 'grid-cols-4'} gap-4 mb-6 p-4 bg-slate-50 rounded-lg`}>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900">{totalLogs}</p>
                <p className="text-xs text-slate-500">Total Logs</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900">{totalUsers}</p>
                <p className="text-xs text-slate-500">Total Users</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{fullySubmittedUsers.length}</p>
                <p className="text-xs text-slate-500">{isMultiDay ? 'Fully Submitted' : 'Submitted'}</p>
              </div>
              {isMultiDay && (
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{partialUsers.length}</p>
                  <p className="text-xs text-slate-500">Partial</p>
                </div>
              )}
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{isMultiDay ? fullyMissingUsers.length : missingCount}</p>
                <p className="text-xs text-slate-500">{isMultiDay ? 'Fully Missing' : 'Missing'}</p>
              </div>
            </div>

            {/* Logs by Team */}
            <div className="space-y-6">
              {Object.values(teamMap).filter(t => t.totalLogs > 0).map(({ team, users: teamUsers }) => (
                <div key={team.id} className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-4 py-3 font-semibold flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    {team.name}
                    <Badge variant="secondary" className="ml-auto">{Object.keys(teamUsers).length} users</Badge>
                  </div>
                  <div className="divide-y">
                    {Object.values(teamUsers).flatMap(({ logs: userLogs }) => 
                      userLogs.map(log => (
                        <div key={log.id} className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <span className="font-semibold text-slate-900">{log.user_name}</span>
                              <span className="text-slate-500 text-sm ml-2">({log.user_email})</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-500">{format(parseISO(log.log_date), "MMM d, yyyy")}</span>
                              {log.day_type ? (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                  {log.day_type.toUpperCase().replace('_', ' ')}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  Submitted
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          {!log.day_type ? (
                            <div className="space-y-3">
                              {(() => {
                                const form = forms.find(frm => frm.id === log.form_id) || forms.find(frm => frm.id === log.assigned_form_id);
                                const fields = form?.fields?.filter(fld => fld.field_type !== 'file') || [
                                  { id: 'tasks_today', label: 'Tasks Worked On Today', field_type: 'rich_text' },
                                  { id: 'challenges', label: 'Challenges and Hurdles', field_type: 'rich_text' },
                                  { id: 'plan_tomorrow', label: 'Plan for Tomorrow', field_type: 'rich_text' },
                                  { id: 'ai_tools_used', label: 'AI Tools Discovery', field_type: 'rich_text' },
                                ];
                                return fields.map(fld => {
                                  const val = log[fld.id];
                                  if (val === undefined || val === null || val === '') return null;
                                  const isNumeric = fld.field_type === 'number' || fld.field_type === 'rating';
                                  return (
                                    <div key={fld.id}>
                                      <p className="text-xs font-medium text-slate-500 uppercase mb-1">{fld.label}</p>
                                      {isNumeric ? (
                                        <div className="text-sm font-semibold text-slate-800 bg-slate-50 p-3 rounded">{val}</div>
                                      ) : (
                                        <div className="text-sm bg-slate-50 p-3 rounded prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: val }} />
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          ) : (
                            <div className="text-center py-6 bg-amber-50 rounded-lg">
                              <span className="text-lg font-bold text-amber-700">
                                {log.day_type.toUpperCase().replace('_', ' ')}
                              </span>
                            </div>
                          )}
                          
                          {/* JD data */}
                          {jdData[log.user_id] && (() => {
                            const jd = jdData[log.user_id];
                            const selfSub = isMultiDay ? (userLogTotals[log.user_id]?.submittals ?? 0) : parseInt(log.num_submittals || 0);
                            const selfInt = isMultiDay ? (userLogTotals[log.user_id]?.interviews ?? 0) : parseInt(log.num_interviews || 0);
                            const hasMismatch = selfSub !== jd.submittals || selfInt !== jd.interviews;
                            return (
                              <div className={`mt-3 rounded-lg p-3 border ${hasMismatch ? 'bg-amber-50 border-amber-300' : 'bg-indigo-50 border-indigo-200'}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-xs font-semibold text-indigo-700">JobDiva ATS vs Self-Reported {isMultiDay ? '(Range)' : ''}</p>
                                  {hasMismatch && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Mismatch</span>}
                                </div>
                                <div className="flex gap-6 text-sm">
                                  <div>
                                    <span className={`font-bold ${selfSub !== jd.submittals ? 'text-amber-700' : 'text-indigo-900'}`}>{jd.submittals}</span>
                                    <span className="text-indigo-600 text-xs ml-1">JD Sub</span>
                                    {selfSub !== jd.submittals && <span className="text-xs text-slate-500 ml-1">(Self: {selfSub})</span>}
                                  </div>
                                  <div>
                                    <span className={`font-bold ${selfInt !== jd.interviews ? 'text-amber-700' : 'text-indigo-900'}`}>{jd.interviews}</span>
                                    <span className="text-indigo-600 text-xs ml-1">JD Int</span>
                                    {selfInt !== jd.interviews && <span className="text-xs text-slate-500 ml-1">(Self: {selfInt})</span>}
                                  </div>
                                  <div>
                                    <span className="font-bold text-indigo-900">{jd.hires}</span>
                                    <span className="text-indigo-600 text-xs ml-1">JD Hire</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          <div className="mt-3 pt-3 border-t text-xs text-slate-400 flex flex-wrap gap-4">
                            <span><Clock className="h-3 w-3 inline mr-1" />{formatDateEST(log.created_at)}</span>
                            <span><Monitor className="h-3 w-3 inline mr-1" />{log.browser_name} / {log.os_name}</span>
                            <span><MapPin className="h-3 w-3 inline mr-1" />{[log.geo_city, log.geo_region, log.geo_country].filter(Boolean).join(", ") || log.ip_address}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}

              {/* Submission Status Section */}
              <div className="space-y-4">
                {/* Fully Submitted */}
                {fullySubmittedUsers.length > 0 && (
                  <div className="border border-green-200 rounded-lg overflow-hidden">
                    <div className="bg-green-50 px-4 py-3 font-semibold text-green-700 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      {isMultiDay ? 'Fully Submitted' : 'Submitted'}
                      <Badge variant="outline" className="ml-auto bg-green-100 text-green-700 border-green-200">{fullySubmittedUsers.length}</Badge>
                    </div>
                    <div className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {fullySubmittedUsers.map(user => (
                          <Badge key={user.id} variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                            {user.full_name}
                            {isMultiDay && ` (${user.submittedDays}/${user.totalDays})`}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Partially Submitted — multi-day only */}
                {isMultiDay && partialUsers.length > 0 && (
                  <div className="border border-amber-200 rounded-lg overflow-hidden">
                    <div className="bg-amber-50 px-4 py-3 font-semibold text-amber-700 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Partially Submitted
                      <Badge variant="outline" className="ml-auto bg-amber-100 text-amber-700 border-amber-200">{partialUsers.length}</Badge>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {partialUsers.map(user => (
                          <div key={user.id} className="p-3 bg-amber-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-slate-800">{user.full_name}</p>
                              <Badge className="bg-amber-100 text-amber-700 text-[10px] hover:bg-amber-100">{user.submittedDays}/{user.totalDays}</Badge>
                            </div>
                            <p className="text-xs text-red-600 mt-1">
                              Missing: {user.missingDates.map(d => format(parseISO(d), "EEE d")).join(", ")}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Fully Missing */}
                {(isMultiDay ? fullyMissingUsers : missingUsers).length > 0 && (
                  <div className="border border-red-200 rounded-lg overflow-hidden">
                    <div className="bg-red-50 px-4 py-3 font-semibold text-red-700 flex items-center gap-2">
                      <XCircle className="h-4 w-4" />
                      {isMultiDay ? 'Fully Missing' : 'Missing Submissions'}
                      <Badge variant="outline" className="ml-auto bg-red-100 text-red-700 border-red-200">{isMultiDay ? fullyMissingUsers.length : missingUsers.length}</Badge>
                    </div>
                    <div className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {(isMultiDay ? fullyMissingUsers : missingUsers).map(user => (
                          <Badge key={user.id} variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                            {user.full_name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 text-center text-xs text-slate-400">
              Generated on {format(new Date(), "MMMM d, yyyy 'at' h:mm a")}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Send Reminder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-slate-900 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {selectedReminderUsers.length} of {reminderUsers.length} Selected
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleAllReminderUsers}
                  className="text-xs h-7"
                >
                  {selectedReminderUsers.length === reminderUsers.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mb-2">Missing log for: {currentRange.to}</p>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {reminderUsers.map((u) => (
                  <label 
                    key={u.id} 
                    className="flex items-center gap-2 p-2 rounded hover:bg-slate-100 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedReminderUsers.includes(u.id)}
                      onChange={() => toggleReminderUserSelection(u.id)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{u.full_name}</p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reminder-message">Message</Label>
              <Textarea
                id="reminder-message"
                placeholder="Enter your reminder message..."
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                rows={4}
                className="resize-none"
                data-testid="reminder-message-input"
              />
              <p className="text-xs text-slate-500">
                You can edit the message or leave it as the default reminder.
              </p>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={ccManager}
                onChange={(e) => setCcManager(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">CC to Manager</span>
              <span className="text-xs text-slate-400">
                (each user's manager will be CC'd)
              </span>
            </label>

            <div className="flex justify-between items-center pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setReminderMessage(DEFAULT_REMINDER_MESSAGE)}
                className="text-slate-500"
              >
                Reset to Default
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setReminderDialogOpen(false)} disabled={sendingReminderDialog}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendReminderFromDialog} 
                  disabled={sendingReminderDialog || selectedReminderUsers.length === 0} 
                  data-testid="send-reminder-btn"
                >
                  {sendingReminderDialog ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send to {selectedReminderUsers.length} User{selectedReminderUsers.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Report Options Dialog */}
      <Dialog open={pdfReportDialogOpen} onOpenChange={setPdfReportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Email PDF Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Report Info */}
            <div className="bg-slate-50 p-3 rounded-lg space-y-1">
              <p className="text-sm text-slate-600">
                <strong>Date:</strong> {currentRange.from === currentRange.to 
                  ? format(parseISO(currentRange.from), "MMM d, yyyy")
                  : `${format(parseISO(currentRange.from), "MMM d")} - ${format(parseISO(currentRange.to), "MMM d, yyyy")}`
                }
              </p>
              <p className="text-sm text-slate-600">
                <strong>Team:</strong> {selectedTeam !== "all" ? teams.find(t => t.id === selectedTeam)?.name : "All Teams"}
              </p>
              {selectedTeam !== "all" && (() => {
                const teamMembers = users.filter(u => u.team_ids?.includes(selectedTeam));
                const managerIds = new Set(teamMembers.flatMap(u => {
                  const rt = u.reports_to;
                  return Array.isArray(rt) ? rt : (rt ? [rt] : []);
                }).filter(Boolean));
                let manager = users.find(u => managerIds.has(u.id) && u.assigned_form_id);
                if (!manager) {
                  manager = teamMembers.find(u => 
                    (u.role === "TEAM_LEADER" || u.role === "ADMIN") && 
                    u.assigned_form_id
                  );
                }
                if (manager) {
                  const managerForm = forms.find(f => f.id === manager.assigned_form_id);
                  return (
                    <p className="text-sm text-blue-700">
                      <strong>Manager:</strong> {manager.full_name} | <strong>Form:</strong> {managerForm?.name || 'Custom'}
                    </p>
                  );
                }
                return null;
              })()}
            </div>

            {/* Form Selection */}
            <div className="space-y-2">
              <Label>Select Form</Label>
              <Select value={selectedFormId || "default"} onValueChange={(val) => handleFormChange(val === "default" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select form" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default Form</SelectItem>
                  {forms.map((form) => (
                    <SelectItem key={form.id} value={form.id}>
                      {form.name} {form.is_default && "(Default)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Field Selection */}
            <div className="space-y-2">
              <Label>Include Fields in Report</Label>
              <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto bg-white">
                {(() => {
                  const currentForm = selectedFormId && selectedFormId !== "default" 
                    ? forms.find(f => f.id === selectedFormId)
                    : null;
                  const fieldsToShow = getFormTextFields(currentForm);
                  
                  return fieldsToShow.map((field) => (
                    <div key={field.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`field-${field.id}`}
                        checked={selectedFormFields.includes(field.id)}
                        onCheckedChange={() => toggleFieldSelection(field.id)}
                      />
                      <label
                        htmlFor={`field-${field.id}`}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {field.label}
                      </label>
                    </div>
                  ));
                })()}
              </div>
              <p className="text-xs text-slate-500">
                Select which fields to include in the PDF report
              </p>
            </div>

            {/* CC Email Addresses */}
            <div className="space-y-2">
              <Label htmlFor="cc-emails">CC Email Addresses</Label>
              <Textarea
                id="cc-emails"
                placeholder="Enter email addresses (comma or space separated)&#10;e.g., manager@company.com, hr@company.com"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                rows={2}
                className="resize-none"
              />
              <p className="text-xs text-slate-500">
                Additional recipients who will receive a copy of this report
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPdfReportDialogOpen(false)} disabled={sendingPdfReport}>
                Cancel
              </Button>
              <Button onClick={handleSendPdfReport} disabled={sendingPdfReport || selectedFormFields.length === 0}>
                {sendingPdfReport ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manager Reports Dialog */}
      <Dialog open={managerReportsDialogOpen} onOpenChange={setManagerReportsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Generate Manager Reports
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-900">
                <strong>Period:</strong> {currentRange.from === currentRange.to 
                  ? format(parseISO(currentRange.from), "MMM d, yyyy")
                  : `${format(parseISO(currentRange.from), "MMM d")} - ${format(parseISO(currentRange.to), "MMM d, yyyy")}`
                }
              </p>
              <p className="text-sm text-purple-900 mt-1">
                <strong>Managers with reports:</strong> {managers.length}
              </p>
            </div>
            
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Select Action:</Label>
              
              <div 
                className={`flex items-start space-x-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  managerReportsOption === 'download' ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setManagerReportsOption('download')}
              >
                <div className="pt-0.5">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    managerReportsOption === 'download' ? 'border-purple-500 bg-purple-500' : 'border-slate-300'
                  }`}>
                    {managerReportsOption === 'download' && <Check className="h-3 w-3 text-white" />}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Download all PDFs (ZIP file)</p>
                  <p className="text-xs text-slate-500 mt-1">Get {managers.length} manager reports in one ZIP file</p>
                </div>
              </div>
              
              <div 
                className={`flex items-start space-x-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  managerReportsOption === 'email' ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setManagerReportsOption('email')}
              >
                <div className="pt-0.5">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    managerReportsOption === 'email' ? 'border-purple-500 bg-purple-500' : 'border-slate-300'
                  }`}>
                    {managerReportsOption === 'email' && <Check className="h-3 w-3 text-white" />}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Email to each manager</p>
                  <p className="text-xs text-slate-500 mt-1">Send individual PDFs via email (CC: taj@jobtalk.ai on all)</p>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                📋 Reports will use form fields assigned to team members. Each manager gets their team&apos;s data only.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setManagerReportsDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateManagerReports} 
              disabled={generatingManagerReports}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {generatingManagerReports ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating {managers.length} Reports...
                </>
              ) : (
                <>
                  {managerReportsOption === 'download' ? <Download className="mr-2 h-4 w-4" /> : <Send className="mr-2 h-4 w-4" />}
                  {managerReportsOption === 'download' ? 'Download ZIP' : 'Send Emails'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MTR Dialog */}
      <Dialog open={mtrDialogOpen} onOpenChange={setMtrDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-indigo-600" />
              Generate MTR (Manager Task Report)
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
              <p className="text-sm text-indigo-900">
                <strong>Period:</strong> {currentRange.from === currentRange.to
                  ? format(parseISO(currentRange.from), "MMM d, yyyy")
                  : `${format(parseISO(currentRange.from), "MMM d")} - ${format(parseISO(currentRange.to), "MMM d, yyyy")}`
                }
              </p>
              <p className="text-sm text-indigo-900 mt-1">
                <strong>Managers with reports:</strong> {managers.length}
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-700">
                <strong>Default form users:</strong> Tasks Worked On Today (grouped by user)
              </p>
              <p className="text-xs text-slate-700 mt-1">
                <strong>Recruiters:</strong> Submittals, Interviews, Starts per day with totals
              </p>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-semibold">Select Action:</Label>

              <div
                className={`flex items-start space-x-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  mtrOption === 'download' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setMtrOption('download')}
              >
                <div className="pt-0.5">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    mtrOption === 'download' ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'
                  }`}>
                    {mtrOption === 'download' && <Check className="h-3 w-3 text-white" />}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Download all PDFs (ZIP file)</p>
                  <p className="text-xs text-slate-500 mt-1">Get {managers.length} MTR reports in one ZIP file</p>
                </div>
              </div>

              <div
                className={`flex items-start space-x-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  mtrOption === 'email' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setMtrOption('email')}
              >
                <div className="pt-0.5">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    mtrOption === 'email' ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'
                  }`}>
                    {mtrOption === 'email' && <Check className="h-3 w-3 text-white" />}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">Email to each manager</p>
                  <p className="text-xs text-slate-500 mt-1">Send individual MTR PDFs via email (CC: taj@jobtalk.ai)</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMtrDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerateMtr}
              disabled={generatingMtr}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {generatingMtr ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating MTR...
                </>
              ) : (
                <>
                  {mtrOption === 'download' ? <Download className="mr-2 h-4 w-4" /> : <Send className="mr-2 h-4 w-4" />}
                  {mtrOption === 'download' ? 'Download ZIP' : 'Send Emails'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
